import * as React from 'react';

import { Form } from '@longline/aqua-ui/containers/Form';
import { Input } from '@longline/aqua-ui/inputs/Input';
import { useToast } from '@longline/aqua-ui/services/Toast/useToast';

import { Guide } from '../../ui/Guide';
import { Pane } from '../../ui/Pane';
import { useAccountContext } from './IAccountContext';
import { AuthApi } from '../../api/services/auth';

const STRONG_PASSWORD_PATTERN = "^(?=.*[A-Za-z])(?=.*\\d)(?=.*[^\\w\\d\\s]).{8,255}$";

const AccountPane = () => {
  const ctx = useAccountContext();
  const toast = useToast();

  const [password1, setPassword1] = React.useState('');
  const [password2, setPassword2] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const handleSavePassword = async () => {
    if (password1 !== password2) {
      toast.toast('Your passwords do not match.', { dismissable: true });
      return;
    }

    try {
      setLoading(true);
      await AuthApi.changePassword(password1);
      toast.toast('Password changed successfully.', { dismissable: true });
      setPassword1('');
      setPassword2('');
      ctx.setMode('view');
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 422) {
        toast.toast('Invalid password.', { dismissable: true });
      } else {
        toast.toast('Unfortunately something went wrong. Please try again.', { dismissable: true });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Pane.Heading>
        <Pane.Title>Account</Pane.Title>
      </Pane.Heading>
      <Pane.Subheader expandable>
        Change password
      </Pane.Subheader>

      <Pane.Body>
        <Form.Field
          rules={{
            required: { message: 'Password is required.' },
            pattern: {
              pattern: STRONG_PASSWORD_PATTERN,
              message: 'Requires letters, digits, and special characters.'
            }
          }}
          name="passw1"
          value={password1}
          label="New password"
          wrapper='box'
          disabled={loading}
          onChange={setPassword1}
          control={(
            <Input
              transparent
              type="password"
              revealable
              placeholder="New password"
              fluid
            />
          )}
          hint="Your new password."
        />

        <Form.Field
          rules={{
            required: { message: 'Password confirmation is required.' },
            pattern: {
              pattern: STRONG_PASSWORD_PATTERN,
              message: 'Requires letters, digits, and special characters.'
            }
          }}
          name="passw2"
          value={password2}
          label="Confirm password"
          wrapper='box'
          disabled={loading}
          onChange={setPassword2}
          control={(
            <Input
              transparent
              type="password"
              revealable
              placeholder="Confirm password"
              fluid
            />
          )}
          hint="Repeat the new password."
        />
      </Pane.Body>

      <Pane.Footer>
        <Pane.CancelButton onClick={() => ctx.setMode('view')}/>
        <Pane.SubmitButton disabled={loading} onClick={handleSavePassword} working={loading}/>
      </Pane.Footer>

      <Pane.Guide>
        <Guide x={0} y={0} title="Account management">
          You can change your single sign-on password here.
        </Guide>
      </Pane.Guide>
    </>
  );
};

export { AccountPane }
